import { FaCheckCircle, FaCheckSquare, FaChevronRight, FaPencilAlt, FaPlus, FaTrash } from "react-icons/fa";

const LedgerClassification = ({
    hasData,
    unclassifiedLedgers,
    selectedLedgerIds,
    setSelectedLedgerIds,
    handleLedgerClick,
    handleDragStart,
    loadingGroups,
    accountTypes,
    colorForAccountType,
    groupsByAccountType,
    ledgersByGroupId,
    dragOverGroupId,
    setDragOverGroupId,
    handleDropOnGroup,
    handleDragOverGroup,
    handleGroupCardClick,
    addingGroupForType,
    newGroupName,
    setNewGroupName,
    creatingGroup,
    handleSaveNewGroup,
    handleCancelAddGroup,
    handleStartAddGroup,
    handleSaveClassification,
    allClassified,
    saving,
    formatSalesValue,
    renamingLedgerId,
    renameValue,
    setRenameValue,
    openRenameInline,
    closeRenameInline,
    handleSaveRename,
    handleDeleteUnclassifiedLedger,
}) => {
    if (!hasData) return null;

    return (
        <div className="grid grid-cols-12 items-start gap-4">
            {/* Unclassified ledgers */}
            <div className="col-span-4">
                <div className="bg-white border border-gray-200 rounded-xl p-3 h-full">
                    <div className="flex items-center justify-between mb-1">
                        <div className="text-base font-semibold text-gray-700">
                            Unclassified Ledgers
                        </div>
                        <span className="text-sm text-gray-600">{unclassifiedLedgers.length} left</span>
                    </div>

                    <p className="text-xs text-gray-600 mb-1 font-medium">
                        Click to select one or more, then click (or drag) into a group.
                    </p>

                    {selectedLedgerIds.size > 0 && (
                        <div className="flex items-center justify-between mb-2 px-2 py-1.5 bg-blue-50 border border-blue-200 rounded-md">
                            <span className="text-xs font-semibold text-blue-800 flex items-center gap-1.5">
                                <FaCheckSquare size={12} />
                                {selectedLedgerIds.size} selected
                            </span>
                            <button
                                type="button"
                                onClick={() => setSelectedLedgerIds(new Set())}
                                className="text-xs font-medium text-blue-700 hover:text-blue-900 transition"
                            >
                                Clear
                            </button>
                        </div>
                    )}

                    <div className="space-y-2 max-h-[650px] overflow-y-auto tiny-scrollbar">
                        {unclassifiedLedgers.map((ledger) => {
                            const isRenaming = renamingLedgerId === ledger.id;

                            return (
                                <div
                                    key={ledger.id}
                                    draggable={!isRenaming}
                                    onDragStart={(e) => handleDragStart(e, ledger.id)}
                                    onClick={() => {
                                        if (isRenaming) return;
                                        handleLedgerClick(ledger.id);
                                    }}
                                    className={`p-2.5 rounded-lg border transition ${isRenaming ? "cursor-default" : "cursor-pointer"} ${selectedLedgerIds.has(ledger.id)
                                        ? "border-blue-500 bg-blue-50 shadow-sm"
                                        : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                                        }`}
                                >
                                    {isRenaming ? (
                                        <div
                                            className="flex items-center gap-1.5"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <input
                                                type="text"
                                                value={renameValue}
                                                onChange={(e) => setRenameValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") handleSaveRename();
                                                    if (e.key === "Escape") closeRenameInline();
                                                }}
                                                autoFocus
                                                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleSaveRename}
                                                className="px-2 py-1 rounded font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 text-xs transition"
                                            >
                                                Save
                                            </button>
                                            <button
                                                type="button"
                                                onClick={closeRenameInline}
                                                className="px-2 py-1 rounded font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 text-xs transition"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-gray-800 truncate">
                                                    {ledger.name}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-0.5 font-medium">
                                                    Dr: {formatSalesValue(ledger.debit)} · Cr:{" "}
                                                    {formatSalesValue(ledger.credit)}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-1 shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openRenameInline(ledger.id, ledger.name);
                                                    }}
                                                    title="Rename ledger"
                                                    className="p-1.5 rounded-full bg-white text-gray-500 hover:bg-blue-100 hover:text-blue-600 transition"
                                                >
                                                    <FaPencilAlt size={11} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteUnclassifiedLedger(ledger.id);
                                                    }}
                                                    title="Remove ledger"
                                                    className="p-1.5 rounded-full bg-white text-gray-500 hover:bg-red-100 hover:text-red-600 transition"
                                                >
                                                    <FaTrash size={11} />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {unclassifiedLedgers.length === 0 && (
                            <div className="flex items-center gap-2 text-sm text-green-600 py-4 justify-center">
                                <FaCheckCircle size={16} />
                                All ledgers classified
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Account types → groups */}
            <div className="col-span-8 space-y-5">
                {loadingGroups && (
                    <div className="text-sm text-gray-500">Loading account chart…</div>
                )}

                {!loadingGroups &&
                    accountTypes.map((accountType) => {
                        const colors = colorForAccountType[accountType];
                        return (
                            <div key={accountType}>
                                <div className="flex items-center justify-between mb-1.5">
                                    <div className="text-base font-semibold text-gray-700 px-1.5">
                                        {accountType}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleStartAddGroup(accountType)}
                                        className="flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900 transition"
                                    >
                                        <FaPlus size={12} />
                                        Add group
                                    </button>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    {groupsByAccountType[accountType].map((group) => {
                                        const groupLedgers = ledgersByGroupId[group.id] ?? [];

                                        return (
                                            <div
                                                key={group.id}
                                                onDrop={(e) => handleDropOnGroup(e, group.id)}
                                                onDragOver={(e) => handleDragOverGroup(e, group.id)}
                                                onDragLeave={() => setDragOverGroupId(null)}
                                                onClick={() => handleGroupCardClick(group.id)}
                                                className={`rounded-xl border-2 border-dashed p-2.5 transition cursor-pointer
                                                                        ${colors.bg} ${dragOverGroupId === group.id
                                                        ? `ring-2 ${colors.ring}`
                                                        : `${colors.bgHover}`
                                                    } ${selectedLedgerIds.size > 0 ? `hover:ring-2 ${colors.hoverRing}` : ""}`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                                                        <FaChevronRight size={12} />
                                                        {group.group}
                                                    </div>
                                                    <span className="text-xs bg-white border border-gray-200 rounded-full px-2 py-0.5 text-gray-600 font-medium">
                                                        {groupLedgers.length}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Add group */}
                                    {addingGroupForType === accountType ? (
                                        <div
                                            className="rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 p-3 flex flex-col gap-2"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <input
                                                type="text"
                                                value={newGroupName}
                                                onChange={(e) => setNewGroupName(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" && !creatingGroup) handleSaveNewGroup();
                                                    if (e.key === "Escape") handleCancelAddGroup();
                                                }}
                                                placeholder="New group name"
                                                autoFocus
                                                disabled={creatingGroup}
                                                className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 disabled:opacity-60"
                                            />
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    type="button"
                                                    onClick={handleCancelAddGroup}
                                                    disabled={creatingGroup}
                                                    className="px-2.5 py-1 rounded-md font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 text-xs transition disabled:opacity-60"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleSaveNewGroup}
                                                    disabled={creatingGroup}
                                                    className="px-2.5 py-1 rounded-md font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 text-xs transition disabled:opacity-60"
                                                >
                                                    {creatingGroup ? "Saving..." : "Save"}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => handleStartAddGroup(accountType)}
                                            className="rounded-xl border-2 border-dashed border-gray-300 p-2.5 flex items-center justify-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 hover:border-gray-400 transition"
                                        >
                                            <FaPlus size={12} />
                                            Add group
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={handleSaveClassification}
                        disabled={!allClassified || saving}
                        className="px-4 py-2 rounded-md font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:shadow-md text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving
                            ? "Saving..."
                            : allClassified
                                ? "Save Classification"
                                : `Classify remaining ${unclassifiedLedgers.length} ledger(s)`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LedgerClassification;